/**
 * 附件上传组件 - 支持多文件上传、预览和删除
 * 选择文件后暂不上传，等待父组件调用上传方法
 */

import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { Upload, message, Button } from 'antd';
import { UploadOutlined, EyeOutlined, DeleteOutlined, PaperClipOutlined } from '@ant-design/icons';
import type { UploadFile, UploadProps, UploadFileStatus } from 'antd/es/upload/interface';
import './index.css';

// 扩展 UploadFile 类型，添加 'pending' 状态
type ExtendedUploadFile = Omit<UploadFile, 'status'> & {
  status?: UploadFileStatus | 'pending';
};

interface AttachmentUploadProps {
  value?: string[]; // 附件URL数组
  onChange?: (urls: string[]) => void;
  disabled?: boolean;
  accept?: string;
}

export interface AttachmentUploadRef {
  uploadFiles: () => Promise<string[]>; // 上传文件并返回URL数组
  getFileList: () => ExtendedUploadFile[]; // 获取当前文件列表
}

const AttachmentUpload = forwardRef<AttachmentUploadRef, AttachmentUploadProps>(({
  value = [],
  onChange,
  disabled = false,
  accept = '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip,.rar,.7z',
}, ref) => {
  const [fileList, setFileList] = useState<ExtendedUploadFile[]>([]);
  const fileMapRef = useRef<Map<string, File>>(new Map()); // 存储实际文件对象
  const fileListRef = useRef<ExtendedUploadFile[]>([]); // 存储最新文件列表状态

  // 根据value同步fileList
  useEffect(() => {
    
    if (value && value.length > 0) {
      const newFileList = value.map((url, index) => {
        const fileName = url.split('/').pop() || `附件${index + 1}`;
        return {
          uid: `existing-${index}`,
          name: fileName,
          status: 'done' as const,
          url: url,
        };
      });
      setFileList(newFileList);
    } else {
      setFileList([]);
    }
  }, [value]);

  // 暴露方法给父组件
  useImperativeHandle(ref, () => ({
    // 上传文件到服务器并返回URL数组
    uploadFiles: async (): Promise<string[]> => {
      const uploadedUrls: string[] = [];
      
      // 使用 Array.from 转换 Map.entries()，避免 downlevelIteration 错误
      const fileEntries = Array.from(fileMapRef.current.entries());
      
      // 1. 上传pending状态的文件
      for (const [uid, file] of fileEntries) {
        const fileInfo = fileList.find(f => f.uid === uid);
        if (fileInfo && fileInfo.status === 'pending') {
          try {
            
            const formData = new FormData();
            formData.append('file', file);
            
            const response = await fetch('/api/biz/news/upload', {
              method: 'POST',
              body: formData,
            });
            
            if (!response.ok) {
              throw new Error('上传失败');
            }
            
            const data = await response.json();
            const fileUrl = data.data || data.url;
            
            
            // 立即更新fileList中的状态
            setFileList(prev => 
              prev.map(f => 
                f.uid === uid 
                  ? { ...f, status: 'done', url: fileUrl, percent: 100 }
                  : f
              )
            );
            
            uploadedUrls.push(fileUrl);
          } catch (error) {
            console.error('Upload failed:', error);
            
            // 更新fileList中的状态为error
            setFileList(prev => 
              prev.map(f => 
                f.uid === uid 
                  ? { ...f, status: 'error' }
                  : f
              )
            );
            
            message.error(`${file.name} 上传失败`);
          }
        }
      }
      
      // 2. 等待状态更新后，再次获取所有done状态的文件URL
      // 需要等到所有setFileList更新完成
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // 3. 获取最新的fileList状态（需要重新读取）
      const latestFileList = fileList.map(f => f); // 复制当前状态
      
      
      const allDoneFiles = latestFileList.filter(f => f.status === 'done' && f.url);
      const existingUrls = allDoneFiles.map(f => f.url!);
      
      
      // 合并所有URLs，避免重复
      const allUrls = Array.from(new Set([...existingUrls, ...uploadedUrls]));
      
      
      return allUrls;
    },
    // 获取当前文件列表
    getFileList: () => fileList,
  }), [fileList]);

  const handleChange: UploadProps['onChange'] = (info) => {
    
    // 由于 beforeUpload 已经阻止默认上传，这里不做任何处理
    // 只在删除操作时才会触发 handleRemove
  };

  const handleRemove = async (file: UploadFile) => {
    
    // 如果是已上传的文件，需要删除服务器上的文件
    if (file.status === 'done' && file.url) {
      try {
        // 从URL中提取文件名
        const fileName = file.url.split('/').pop() || file.name;
        
        const response = await fetch(`/api/biz/news/deleteFile?fileName=${encodeURIComponent(fileName)}`, {
          method: 'DELETE',
        });
        
        if (response.ok) {
          const result = await response.json();
        } else {
          console.error('服务器文件删除失败');
        }
      } catch (error) {
        console.error('删除服务器文件失败:', error);
      }
    }
    
    // 从fileList中移除
    const newFileList = fileList.filter(item => item.uid !== file.uid);
    setFileList(newFileList);
    
    // 从fileMap中移除
    fileMapRef.current.delete(file.uid);
    
    // 通知父组件 - 通知时只需要已上传完成的文件URL
    const urls = newFileList
      .filter(f => f.status === 'done' && f.url)
      .map(f => f.url!);
    onChange?.(urls);
    
    message.success('附件已删除');
  };

  const handlePreview = (file: UploadFile) => {
    const fileUrl = file.url || file.thumbUrl;
    if (fileUrl) {
      window.open(fileUrl, '_blank');
    }
  };

  const beforeUpload = (file: File): boolean => {
    
    // 验证文件大小（100MB）
    const isLt100M = file.size / 1024 / 1024 < 100;
    if (!isLt100M) {
      message.error('附件大小不能超过 100MB！');
      return false;
    }
    
    // 添加到fileList，状态为pending，暂不上传
    const newFile: ExtendedUploadFile = {
      uid: Date.now().toString(),
      name: file.name,
      status: 'pending',
      percent: 0,
    };
    
    setFileList(prev => [...prev, newFile]);
    fileMapRef.current.set(newFile.uid, file);
    
    // 通知父组件
    const urls = fileList
      .filter(f => f.status === 'done' && f.url)
      .map(f => f.url!);
    onChange?.(urls);
    
    message.success(`${file.name} 已添加到附件列表`);
    
    return false; // 阻止默认上传
  };

  return (
    <>
      <Upload
        fileList={fileList as UploadFile[]}
        onChange={handleChange}
        beforeUpload={beforeUpload}
        onRemove={handleRemove}
        accept={accept}
        multiple
        disabled={disabled}
        showUploadList={true}
        itemRender={(originNode, file, fileList, actions) => {
          // 将 file 转换为 ExtendedUploadFile 类型
          const extendedFile = file as ExtendedUploadFile;
          const isLoading = file.status === 'uploading';
          const isPending = extendedFile.status === 'pending';
          
          return (
            <div className="attachment-upload-item" style={{ marginBottom: 8 }}>
              <div className="attachment-info">
                <PaperClipOutlined style={{ marginRight: 8 }} />
                <span className="attachment-name">{file.name}</span>
                {isLoading && <span className="attachment-status">(上传中...)</span>}
                {isPending && <span className="attachment-status" style={{ color: '#ff9800' }}>(待上传)</span>}
              </div>
              <div className="attachment-actions">
                {file.status === 'done' && (
                  <>
                    <Button 
                      type="text" 
                      size="small" 
                      icon={<EyeOutlined />}
                      onClick={() => handlePreview(file)}
                      title="预览"
                    />
                    <Button 
                      type="text" 
                      size="small" 
                      icon={<DeleteOutlined />}
                      danger
                      onClick={() => handleRemove(file)}
                      title="删除"
                    />
                  </>
                )}
                {extendedFile.status === 'pending' && (
                  <Button 
                    type="text" 
                    size="small" 
                    icon={<DeleteOutlined />}
                    danger
                    onClick={() => handleRemove(file)}
                    title="删除"
                  />
                )}
              </div>
            </div>
          );
        }}
      >
        <Button icon={<UploadOutlined />}>上传附件（支持多选）</Button>
      </Upload>
      <div style={{ marginTop: 8, color: '#999', fontSize: '12px' }}>
        支持格式：PDF、Word、Excel、PPT、TXT、ZIP等
      </div>
    </>
  );
});

AttachmentUpload.displayName = 'AttachmentUpload';

export default AttachmentUpload;
